import {
  ResourceView,
  type ActiveFiltersState,
  type Filter,
  type FilterDefinition,
  type FilterMatchMode,
  type FilterMenuOutput,
} from "@/shared/components/ResourceView";
import { type Updater } from "@/shared/foundation/primitives/Table";

import styles from "./DashboardFilterBar.module.css";

export interface DashboardFilterBarProps {
  /** Committed filter state. */
  active: ActiveFiltersState;
  /** Filterable session fields offered in the menu. */
  definitions: FilterDefinition[];
  /** Fired when a filter is committed. */
  onFilterSelected: (output: FilterMenuOutput) => void;
  /** Fired with the index of the chip the user removed (`×`). */
  onRemove: (index: number) => void;
  /** Fired with a chip's index and its edited value. */
  onUpdate: (index: number, filter: Filter) => void;
  /** Fired when the user clears all filters. */
  onClear: () => void;
  /** Fired when the AND/OR picker (shown at 2+ chips) changes. */
  onMatchModeChange: (updater: Updater<FilterMatchMode>) => void;
}

/**
 * Filter dashboard metrics by resource properties
 */
export function DashboardFilterBar({
  active,
  definitions,
  onFilterSelected,
  onRemove,
  onUpdate,
  onClear,
  onMatchModeChange,
}: DashboardFilterBarProps) {
  // The dashboard only ever commits structured filters (never RoboQL).
  const hasFilters = active.type === "structured" && active.filters.length > 0;

  if (hasFilters) {
    return (
      <ResourceView.ActiveFilters
        filters={active}
        filterDefinitions={definitions}
        onFilterSelected={onFilterSelected}
        onRemove={onRemove}
        onUpdate={onUpdate}
        onClear={onClear}
        onMatchModeChange={onMatchModeChange}
        metrics={false}
      />
    );
  }

  return (
    <div className={styles.bar}>
      <ResourceView.Filters
        filterDefinitions={definitions}
        onFilterSelected={onFilterSelected}
        metrics={false}
      />
    </div>
  );
}
